import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
    listDataDeletionRequests,
    processDataDeletionRequest,
} from "../services/api";
import "../styles/dataPrivacy.css";
import { DELETION_REQUESTS_CHANGED } from "../hooks/usePendingDeletionRequests";

const statusOptions = ["pending", "approved", "rejected", "completed"];

const formatDate = (value) => {
    if (!value) {
        return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "-";
    }
    return parsed.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function DataDeletionManagement() {
    const { session } = useAuth();
    const [requests, setRequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeRequest, setActiveRequest] = useState(null);
    const [decision, setDecision] = useState(null);
    const [reason, setReason] = useState("");

    const accessToken = session?.access_token || null;

    const loadRequests = useCallback(async () => {
        if (!accessToken) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await listDataDeletionRequests({
                status: statusFilter,
                page,
                limit,
                accessToken,
            });
            setRequests(response.data || []);
            setTotal(response.total || 0);
        } catch (err) {
            setError(err?.message || "Unable to load deletion requests.");
        } finally {
            setLoading(false);
        }
    }, [accessToken, limit, page, statusFilter]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const openDecision = (request, nextDecision) => {
        setActiveRequest(request);
        setDecision(nextDecision);
        setReason("");
    };

    const closeDecision = () => {
        setActiveRequest(null);
        setDecision(null);
        setReason("");
    };

    const handleProcess = async () => {
        if (!activeRequest || !decision || !accessToken) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await processDataDeletionRequest({
                requestId: activeRequest.id,
                approved: decision === "approve",
                reason: reason.trim(),
                accessToken,
            });
            window.dispatchEvent(new Event(DELETION_REQUESTS_CHANGED));
            closeDecision();
            await loadRequests();
        } catch (err) {
            setError(err?.message || "Unable to process deletion request.");
        } finally {
            setLoading(false);
        }
    };

    const hasRequests = useMemo(() => requests.length > 0, [requests]);
    const totalPages = Math.max(Math.ceil(total / limit), 1);

    return (
        <main className="data-privacy-page">
            <div className="data-privacy-shell">
                <section className="data-privacy-card">
                    <div className="data-privacy-header">
                        <div>
                            <h1>Deletion requests</h1>
                            <p className="helper-text">
                                Review and process credential deletion requests.
                            </p>
                        </div>
                        <button
                            className="btn ghost"
                            type="button"
                            onClick={loadRequests}
                            disabled={loading}
                        >
                            Refresh
                        </button>
                    </div>
                    <div className="filter-row">
                        <label>
                            Status
                            <select
                                value={statusFilter}
                                onChange={(event) => {
                                    setStatusFilter(event.target.value);
                                    setPage(1);
                                }}
                            >
                                {statusOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    {loading && <p className="helper-text">Loading...</p>}
                    {!loading && !hasRequests && (
                        <p className="helper-text">No requests found.</p>
                    )}
                    {hasRequests && (
                        <div className="data-privacy-table">
                            <div className="table-row table-header">
                                <span>Request ID</span>
                                <span>Credential</span>
                                <span>Requester</span>
                                <span>Status</span>
                                <span>Requested</span>
                                <span>Actions</span>
                            </div>
                            {requests.map((request) => (
                                <div className="table-row" key={request.id}>
                                    <span className="mono">{request.id}</span>
                                    <span>
                                        <div>{request.credential_id}</div>
                                        <div className="text-muted">
                                            {request.credentials?.company || "-"} ·{" "}
                                            {request.credentials?.provider || "-"}
                                        </div>
                                    </span>
                                    <span>{request.requester_email}</span>
                                    <span className={`status ${request.status}`}>
                                        {request.status}
                                    </span>
                                    <span>{formatDate(request.requested_at)}</span>
                                    <span className="actions">
                                        {request.status === "pending" && (
                                            <>
                                                <button
                                                    className="btn primary"
                                                    type="button"
                                                    onClick={() =>
                                                        openDecision(
                                                            request,
                                                            "approve"
                                                        )
                                                    }
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    className="btn ghost"
                                                    type="button"
                                                    onClick={() =>
                                                        openDecision(
                                                            request,
                                                            "reject"
                                                        )
                                                    }
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                        {request.status !== "pending" && (
                                            <span className="text-muted">-</span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={loading || page <= 1}
                            >
                                Previous
                            </button>
                            <span className="page-status">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() =>
                                    setPage((prev) =>
                                        Math.min(prev + 1, totalPages)
                                    )
                                }
                                disabled={loading || page >= totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </section>
            </div>
            {activeRequest && decision && (
                <div className="data-privacy-modal">
                    <div className="modal-overlay" onClick={closeDecision} />
                    <div className="modal-card">
                        <h2>
                            {decision === "approve"
                                ? "Approve deletion"
                                : "Reject deletion"}
                        </h2>
                        <p className="helper-text">
                            Provide a reason (optional). This will be sent to the
                            requester.
                        </p>
                        <textarea
                            rows={4}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            placeholder="Reason for decision"
                        />
                        <div className="modal-actions">
                            <button
                                className="btn primary"
                                type="button"
                                onClick={handleProcess}
                                disabled={loading}
                            >
                                {loading ? "Saving..." : "Confirm"}
                            </button>
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={closeDecision}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
